import dynamic from 'next/dynamic'
import { DeclaredProvider, useDeclaredContext, useDeclaredState } from 'lib/owner-notes/declared'
import { LabCompareProvider, useLabCompare } from 'lib/owner-notes/lab-compare'

// The lab dialog and drawer carry the PDP's chart panel (LabChartPanel), so they mount — and
// their chunks load — only once the page has something to draw in them, never with the page:
// mounting them for every visitor downloaded ~2k lines (audit 2026-09-13, compare-1).
const LabCompareDialog = dynamic(() => import('components/owner-notes/LabCompareDialog'), {
  ssr: false,
})
const LabCompareDrawer = dynamic(() => import('components/owner-notes/LabCompareDrawer'), {
  ssr: false,
})

/**
 * Whether the page-level lab surfaces are drawn right now — the one condition `Surfaces` mounts
 * on, read from the providers. A focused pick (the guide row's Measurements / Owners on desktop,
 * 2026-10-18) is the panel's reason to exist as much as a pin is. False outside the providers.
 */
function useLabSurfacesMounted(mountOnPin: boolean): boolean {
  const declared = useDeclaredContext()?.declared ?? null
  const lab = useLabCompare()
  const pinned = lab?.pinned.length ?? 0
  const focused = lab?.focus !== null && lab?.focus !== undefined
  return Boolean(declared) || (mountOnPin && (pinned > 0 || focused))
}

/**
 * For the article templates: true while the LabCompare drawer / pill is docked at the bottom of
 * a phone (the surfaces are mounted, `mountOnPin`), so another fixed bottom surface — the
 * conversation bar (`ArticleConversation` `hideBar`) — yields only then, not on every page that
 * carries a strip. False on a page without the lab providers.
 */
export function useLabSurfacesDocked(): boolean {
  return useLabSurfacesMounted(true)
}

/** The dialog + pinned drawer, mounted only when the page can use them. Inside the providers,
 *  because the pin count is lab-compare state. */
const Surfaces: React.VFC<{ mountOnPin: boolean; versus: boolean }> = ({ mountOnPin, versus }) => {
  if (!useLabSurfacesMounted(mountOnPin)) {
    return null
  }
  return (
    <>
      <LabCompareDialog />
      <LabCompareDrawer versus={versus} />
    </>
  )
}

/** `DeclaredState` isn't exported from lib/owner-notes/declared; its owner hook's return is it. */
type DeclaredState = ReturnType<typeof useDeclaredState>

interface LabSurfacesProps {
  /** The page's one declared-product state (`useDeclaredState()` in the template): the
   *  capture surface writes it, every card / pick reads it (owner-notes contract #63). */
  declared: DeclaredState
  /** Also mount the surfaces once something is pinned with nothing declared. The best-list
   *  guide wants this (an undeclared reader can pin two picks against each other — the
   *  drawer with no "yours" curve, 2026-09-15); the category page mounts on a declared pair
   *  only. */
  mountOnPin?: boolean
  /** Draw the drawer's comparison as the review's versus panel (`ReviewVersusPanel`: scores,
   *  lab data, specs and owners side by side) instead of the chart alone. The review page. */
  versus?: boolean
  children?: React.ReactNode
}

/**
 * The owner-notes lab-compare shell a listing page wraps its body in: the declared-product
 * and lab-compare providers, plus the page-level lab dialog and pinned drawer. Shared by the
 * category page and the best-list guide so the dynamic imports and provider nesting live in
 * one place; each page keeps its own mount condition via `mountOnPin`.
 */
const LabSurfaces: React.VFC<LabSurfacesProps> = ({
  declared,
  mountOnPin = false,
  versus = false,
  children,
}) => (
  <DeclaredProvider value={declared}>
    {/* The drawer draws yours beside the pins (not the review's versus panel): it takes a slot. */}
    <LabCompareProvider reserved={!versus && declared.declared ? 1 : 0}>
      {children}
      <Surfaces mountOnPin={mountOnPin} versus={versus} />
    </LabCompareProvider>
  </DeclaredProvider>
)

export default LabSurfaces
